import { CommonModule } from '@angular/common';
import { Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { NgxPaginationModule } from 'ngx-pagination';
import { ComplainFormStatus } from 'src/app/enumeration/complain-form-status.enum';
import { PageSize } from 'src/app/enumeration/pageSize';
import { UserLoginRole } from 'src/app/enumeration/roles';
import { ComplainService } from 'src/app/services/complain/complain-service';
import { CheckAccessRoles } from 'src/app/utils/check-access-roles';

@Component({
  selector: 'app-complain-list',
  imports: [
    NgxPaginationModule,
    CommonModule,
    FormsModule],
  templateUrl: './complain-list.html',
  styleUrls: ['./complain-list.css']
})
export class ComplainList implements OnInit {
[x: string]: any;

  private complainService = inject(ComplainService);
  private destroyRef = inject(DestroyRef);
  private router = inject(Router);
  private checkAccessRoles = inject(CheckAccessRoles);

  complainStatuses = Object.values(ComplainFormStatus);
  selectedStatus = signal<ComplainFormStatus>(ComplainFormStatus.ALL);

  complaints = signal<any[]>([]);
  showModal = signal<boolean>(false);
  isLoading = signal<boolean>(false);
  isDownloadAccess = signal<boolean>(false);
  isDownloading = signal<boolean>(false);

  newTitle = signal<string>('');
  newDescription = signal<string>('');

  pageSize = signal<number>(PageSize.COMPLAIN_LIST);
  allTotal = signal<number>(0);
  page = signal<number>(0);

  filterStartDate = signal<string>('');
  filterEndDate = signal<string>('');
  downloadFileName = signal<string>('');

  isFormInvalid = computed(() => {
    return !this.newTitle().trim() || !this.newDescription().trim();
  });

  ngOnInit(): void {

    this.isDownloadAccess.set(this.checkAccessRoles.check([UserLoginRole.SUPER_ADMIN, UserLoginRole.ADMIN, UserLoginRole.DEALER_ADMIN]));

    this.loadComplaints();
  }

  loadComplaints(): void {
    this.isLoading.set(true);
    this.complainService.getComplainList(this.selectedStatus(), this.filterStartDate(), this.filterEndDate(), this.page(), this.pageSize()).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (res) => {
        this.complaints.set(res.complainInfoRespLst);
        this.isLoading.set(false);
      },
      error: (err) => {
        console.error('Error fetching list', err);
        this.isLoading.set(false);
      }
    });
  }

  onSubmit(): void {
    if (this.isFormInvalid()) return;

    this.complainService.createComplain(this.newTitle(), this.newDescription()).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (newComplain) => {
        this.complaints.update(current => [newComplain, ...current]);
        this.resetForm();
      },
      error: (err) => console.error('Error creating complaint', err)
    });
  }

  applyFilters(): void {

    const hasStart = !!this.filterStartDate().trim();
    const hasEnd = !!this.filterEndDate().trim();

    // 1. Validation Rule: Check if one date is provided but the other is missing
    if (hasStart !== hasEnd) {
      console.warn('Both Start Date and End Date are required when filtering by date.');
      return;
    }

    // 2. Validation Rule: If date ranges exist, evaluate historical order alignment
    if (hasStart && hasEnd) {
      const startDateObj = this.parseDateString(this.filterStartDate());
      const endDateObj = this.parseDateString(this.filterEndDate());

      if (!startDateObj || !endDateObj) {
        console.warn('Invalid date format detected. Please use DD/MM/YYYY.');
        return;
      }

      if (endDateObj < startDateObj) {
        console.warn('End Date cannot be earlier than the Start Date.');
        return;
      }
    }

    this.page.set(0); // Reset to first page when filters are applied
    this.loadComplaints();
  }

  downloadReport(): void {
    if (!this.downloadFileName().trim()) {
      console.warn('Custom file name is required for download.');
      return;
    }

    let fileName = this.downloadFileName().endsWith('.csv') ? this.downloadFileName() : `${this.downloadFileName()}.csv`;

    this.isDownloading.set(true);

    this.complainService.getDownloadFile(fileName, this.selectedStatus(), this.filterStartDate(), this.filterEndDate()).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (value) => {
        this.downloadFileInLocal(value);
        this.isDownloading.set(false);
      },
      error: (err) => {
        console.error('Error downloading file', err);
        this.isDownloading.set(false);
      }
    });
  }

  clearFilters(): void {
    this.filterStartDate.set('');
    this.filterEndDate.set('');
    this.selectedStatus.set(ComplainFormStatus.ALL);
    this.page.set(0); // Reset to first page when filters are cleared
    this.loadComplaints();
  }

  navigateToDetail(complainId: any) {
    this.router.navigate(['/nav/complainDetails', complainId], { skipLocationChange: true })
  }

  resetForm(): void {
    this.newTitle.set('');
    this.newDescription.set('');
    this.showModal.set(false);
  }

  handlePageChange(event: number): void {
    if (!this.isLoading()) {
      this.page.set(event - 1);
      this.loadComplaints();
    }
  }

  onPickerSelect(event: Event, field: 'start' | 'end'): void {
    const input = event.target as HTMLInputElement;
    if (!input.value) return;

    // Splits native YYYY-MM-DD output string pattern
    const [year, month, day] = input.value.split('-');
    const formattedDate = `${day}/${month}/${year}`;

    if (field === 'start') {
      this.filterStartDate.set(formattedDate);
    } else {
      this.filterEndDate.set(formattedDate);
    }
  }

  private parseDateString(dateStr: string): Date | null {
    if (!dateStr || dateStr.length !== 10) return null;
    const [day, month, year] = dateStr.split('/').map(Number);
    // Note: Months are 0-indexed in native JavaScript Date constructor arrays
    const date = new Date(year, month - 1, day);
    return isNaN(date.getTime()) ? null : date;
  }

  onDateInput(event: Event, field: 'start' | 'end'): void {
    const input = event.target as HTMLInputElement;
    let value = input.value.replace(/\D/g, ''); // Strip all non-digit values

    // Inject forward-slashes depending on text length thresholds
    if (value.length > 2 && value.length <= 4) {
      value = `${value.slice(0, 2)}/${value.slice(2)}`;
    } else if (value.length > 4) {
      value = `${value.slice(0, 2)}/${value.slice(2, 4)}/${value.slice(4, 8)}`;
    }

    // Force bound values onto template target DOM nodes
    input.value = value;

    if (field === 'start') {
      this.filterStartDate.set(value);
    } else {
      this.filterEndDate.set(value);
    }
  }

  downloadFileInLocal(data: string) {
    const blob = new Blob([data], { type: 'text/csv' });
    const download = document.createElement('a');
    download.href = URL.createObjectURL(blob);
    download.setAttribute('download', this.downloadFileName());
    document.body.appendChild(download);
    download.click();
    document.body.removeChild(download);
    this.downloadFileName.set(''); // Reset the file name after download'';
  }
}
